import { IconAlertTriangle, IconLoader2, IconSparkles } from "@tabler/icons-react"
import { type FC, useEffect, useMemo, useRef, useState } from "react"

import Button from "~/components/Button"
import { DefaultModal } from "~/components/Modal"
import { useAlert } from "~/contexts/AlertContext"
import { useUser } from "~/contexts/UserContext"
import { query } from "~/utilities/trpc"

const LOADING_MESSAGES = [
	"Fetching each selected page",
	"Reading the page content",
	"Matching images to nearby copy",
	"Choosing the best Forento blocks",
	"Migrating pages one by one",
	"Applying your brand",
	"Building the new layout",
	"Setting up your blocks",
	"Almost there",
]

const ROTATION_INTERVAL_MS = 2_800
const FADE_DURATION_MS = 300

const shuffle = <T,>(items: readonly T[]): T[] => {
	const result = [...items]
	for (let i = result.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1))
		;[result[i], result[j]] = [result[j]!, result[i]!]
	}
	return result
}

type SitemapPage = { url: string; path: string; title: string }
type PageGroup = { key: string; title: string; pages: SitemapPage[] }

type Props = { isOpen: boolean; onClose(): void }
const UcraftImageSelectionModal: FC<Props> = ({ isOpen, onClose }) => {
	const user = useUser()
	const alert = useAlert()
	const [selectedUrls, setSelectedUrls] = useState<Set<string>>(new Set())
	const [messageIndex, setMessageIndex] = useState(0)
	const [isMessageVisible, setIsMessageVisible] = useState(true)
	const [progress, setProgress] = useState(0)
	const hasInitializedSelection = useRef(false)

	const sitemapPages = query.landing.listUcraftMigrationPages.useQuery(undefined, { enabled: isOpen })
	const startMigration = query.landing.startUcraftMigration.useMutation({
		async onSuccess() {
			await user.reload()
			onClose()
		},
		async onError(error) {
			console.error(error)
			await alert.show("Error", "Failed to migrate the Ucraft site. Please try again.")
		},
	})

	useEffect(() => {
		if (!isOpen) {
			setSelectedUrls(new Set())
			setMessageIndex(0)
			setIsMessageVisible(true)
			setProgress(0)
			hasInitializedSelection.current = false
			return
		}
		if (sitemapPages.data !== undefined && !hasInitializedSelection.current) {
			setSelectedUrls(new Set(sitemapPages.data.map(page => page.url)))
			hasInitializedSelection.current = true
		}
	}, [isOpen, sitemapPages.data])

	const shuffledMessages = useMemo(() => shuffle(LOADING_MESSAGES), [])
	const isMigrating = startMigration.isPending

	useEffect(() => {
		if (!isMigrating) return

		setProgress(0)

		const progressInterval = setInterval(() => {
			setProgress(prev =>
				prev >= 93 ? prev : prev >= 85 ? prev + 0.05 : prev >= 70 ? prev + 0.1 : Math.min(85, prev + 0.2),
			)
		}, 50)

		const rotate = setInterval(() => {
			setIsMessageVisible(false)
			const fade = setTimeout(() => {
				setMessageIndex(prev => (prev + 1) % shuffledMessages.length)
				setIsMessageVisible(true)
			}, FADE_DURATION_MS)
			return () => clearTimeout(fade)
		}, ROTATION_INTERVAL_MS)

		return () => {
			clearInterval(progressInterval)
			clearInterval(rotate)
		}
	}, [isMigrating, shuffledMessages])

	const pages = sitemapPages.data ?? []
	const pageGroups = useMemo(() => groupPagesByTopLevelPath(pages), [pages])
	const selectedCount = selectedUrls.size
	const isAllSelected = pages.length > 0 && selectedCount === pages.length

	const togglePage = (url: string, isSelected: boolean) => {
		setSelectedUrls(prev => {
			const next = new Set(prev)
			if (isSelected) next.add(url)
			else next.delete(url)
			return next
		})
	}

	const toggleAll = () => {
		setSelectedUrls(isAllSelected ? new Set() : new Set(pages.map(page => page.url)))
	}

	const setGroupSelected = (group: PageGroup, isSelected: boolean) => {
		setSelectedUrls(prev => {
			const next = new Set(prev)
			for (const page of group.pages) {
				if (isSelected) next.add(page.url)
				else next.delete(page.url)
			}
			return next
		})
	}

	const startSelectedMigration = () => {
		startMigration.mutate({ pageUrls: pages.filter(page => selectedUrls.has(page.url)).map(page => page.url) })
	}

	return (
		<DefaultModal onClose={onClose} width={900} isOpen={isOpen}>
			{isMigrating ? (
				<div className="flex flex-col items-center gap-7 py-8 text-center">
					<div className="relative">
						<div className="bg-primary/20 absolute inset-0 animate-ping rounded-full" />
						<div className="bg-primary/10 absolute inset-0 animate-pulse rounded-full" />
						<div className="from-primary/15 to-primary/5 ring-primary/5 relative flex size-20 items-center justify-center rounded-full bg-linear-to-br ring-8">
							<IconSparkles className="text-primary size-10" strokeWidth={1.75} />
						</div>
					</div>

					<div className="flex max-w-lg flex-col gap-2">
						<h3 className="text-2xl font-semibold text-gray-900">Migrating your site</h3>
						<span
							className="min-h-6 text-base text-gray-600 transition-opacity"
							style={{ opacity: isMessageVisible ? 1 : 0, transitionDuration: `${FADE_DURATION_MS}ms` }}
						>
							{shuffledMessages[messageIndex]}
						</span>
					</div>

					<div className="flex w-full max-w-md flex-col gap-2">
						<div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
							<div
								className="from-primary/70 to-primary h-full rounded-full bg-linear-to-r transition duration-700 ease-out"
								style={{ width: `${progress}%` }}
							/>
						</div>
						<div className="w-full text-center text-xs font-medium text-gray-500 tabular-nums">
							{Math.round(progress)}%
						</div>
					</div>
				</div>
			) : (
				<div className="flex flex-col gap-6 px-8 py-2">
					<div className="flex flex-col gap-2">
						<h3 className="text-2xl font-semibold text-gray-900">Choose pages to migrate</h3>
						<p className="max-w-2xl text-sm leading-6 text-gray-600">
							We found these pages from the Ucraft sitemap. Selected pages will be migrated one by one
							into the new Forento editor.
						</p>
					</div>

					{sitemapPages.isLoading ? (
						<div className="flex min-h-48 items-center justify-center gap-3 text-gray-600">
							<IconLoader2 className="size-5 animate-spin" />
							<span>Fetching sitemap</span>
						</div>
					) : sitemapPages.error ? (
						<div className="flex min-h-48 flex-col items-center justify-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-6 text-center text-amber-900">
							<IconAlertTriangle className="size-7" />
							<p className="font-semibold">Could not fetch the sitemap.</p>
							<p className="max-w-md text-sm">
								Close this dialog and try again. If it keeps failing, the Ucraft site may not have a
								readable sitemap.
							</p>
						</div>
					) : (
						<div className="flex flex-col gap-3">
							<div className="flex items-center justify-between gap-3">
								<button
									type="button"
									onClick={toggleAll}
									className="text-primary hover:text-primary/80 cursor-pointer text-sm font-semibold"
								>
									{isAllSelected ? "Deselect all" : "Select all"}
								</button>
								<span className="text-sm font-medium text-gray-500">
									{selectedCount} of {pages.length} selected
								</span>
							</div>
							<div className="max-h-96 overflow-y-auto rounded-lg border border-gray-200">
								{pageGroups.map(group => (
									<div key={group.key} className="border-b border-gray-200 last:border-b-0">
										<div className="flex items-center justify-between gap-3 bg-gray-50 px-4 py-2">
											<div className="min-w-0">
												<p className="truncate text-sm font-semibold text-gray-900">
													{group.title}
												</p>
												<p className="text-xs text-gray-500">
													{countSelectedPages(group.pages, selectedUrls)} of{" "}
													{group.pages.length} selected
												</p>
											</div>
											<div className="flex shrink-0 items-center gap-3">
												<button
													type="button"
													onClick={() => setGroupSelected(group, true)}
													className="text-primary hover:text-primary/80 cursor-pointer text-xs font-semibold"
												>
													Select
												</button>
												<button
													type="button"
													onClick={() => setGroupSelected(group, false)}
													className="cursor-pointer text-xs font-semibold text-gray-500 hover:text-gray-800"
												>
													Deselect
												</button>
											</div>
										</div>
										{group.pages.map(page => (
											<label
												key={page.url}
												className="flex cursor-pointer items-start gap-3 border-t border-gray-100 px-4 py-3 hover:bg-gray-50"
											>
												<input
													type="checkbox"
													checked={selectedUrls.has(page.url)}
													onChange={event => togglePage(page.url, event.target.checked)}
													className="accent-primary mt-1 size-4"
												/>
												<span className="min-w-0 flex-1">
													<span className="block truncate text-sm font-semibold text-gray-900">
														{page.title}
													</span>
													<span className="block truncate text-xs text-gray-500">
														{page.path}
													</span>
												</span>
											</label>
										))}
									</div>
								))}
							</div>
						</div>
					)}

					<div className="flex justify-end gap-3">
						<Button variant="secondary" onClick={onClose} isDisabled={startMigration.isPending}>
							Cancel
						</Button>
						<Button
							variant="primary"
							onClick={startSelectedMigration}
							isDisabled={selectedCount === 0 || sitemapPages.isLoading || sitemapPages.error !== null}
						>
							Migrate selected pages
						</Button>
					</div>
				</div>
			)}
		</DefaultModal>
	)
}

function groupPagesByTopLevelPath(pages: SitemapPage[]): PageGroup[] {
	const groups = new Map<string, PageGroup>()
	for (const page of pages) {
		const key = getTopLevelPath(page.path)
		const existing = groups.get(key)
		if (existing !== undefined) {
			existing.pages.push(page)
			continue
		}
		groups.set(key, { key, title: getGroupTitle(key), pages: [page] })
	}
	return [...groups.values()]
}

function getTopLevelPath(path: string): string {
	const firstSegment = path.split("/").filter(Boolean)[0]
	return firstSegment === undefined ? "/" : `/${firstSegment}`
}

function getGroupTitle(path: string): string {
	if (path === "/") return "Home"
	return path
}

function countSelectedPages(pages: SitemapPage[], selectedUrls: Set<string>): number {
	return pages.filter(page => selectedUrls.has(page.url)).length
}

export default UcraftImageSelectionModal
